import { DocumentTextIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { FilePreview } from "components/cards";
import parse from "html-react-parser";
import lodash from "lodash";
import moment from "moment";
import { FC } from "react";
import _ from "lodash";

interface IssueDetailsViewProps {
  issue: {
    code: string;
    issueBackground: string;
    issueDetails: string;
    action: string;
    expectation: string;
    attachments: string[];
    classification: string;
    priority: string;
    contactPerson: {
      fullName: string;
      email: string;
      phoneNumber: string;
    };
    clickupTicketId: string;
    createdBy: any;
    createdByType: string;
    meta: {
      ipAddress: string;
      userAgent: string;
      application: string;
    };
    status: string;
    stages: {
      user: string;
      userType: string;
      stage: string;
      notes?: string;
      escalatedTo?: string;
      timestamp: Date;
    }[];
    currentStage: {
      user: string;
      userType: string;
      stage: string;
      notes?: string;
      escalatedTo?: string;
      timestamp: Date;
    };
    createdAt: Date;
    updatedAt: Date;
  };
}

export const IssueDetailsView: FC<IssueDetailsViewProps> = ({ issue }) => {
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-3 p-6 overflow-y-auto">
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Code</dt>
        <dd className="mt-1 text-sm text-gray-900">{issue?.code}</dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Classification</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {_.startCase(issue?.classification) || "N/A"}
        </dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Stage</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {_.startCase(issue?.currentStage?.stage) || "N/A"}
        </dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Status</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {_.startCase(issue?.status) || "N/A"}
        </dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Opened At</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {moment(issue?.createdAt).format("DD/MM/YYYY hh:mm A") || "N/A"}
        </dd>
      </div>

      <div className="!col-start-1 sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Contact Person</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.contactPerson?.fullName || "N/A"}
        </dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Contact Phone</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.contactPerson?.phoneNumber || "N/A"}
        </dd>
      </div>
      <div className="sm:col-span-1">
        <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.contactPerson?.email || "N/A"}
        </dd>
      </div>
      {issue?.clickupTicketId && (
        <div className="sm:col-span-1">
          <dt className="text-sm font-medium text-gray-500">Ticket ID</dt>
          <dd className="mt-1 text-sm text-gray-900">
            {issue?.clickupTicketId || "N/A"}
          </dd>
        </div>
      )}
      <div className="sm:col-span-3">
        <dt className="text-sm font-medium text-gray-500">Background</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.issueBackground ? parse(issue?.issueBackground) : "N/A"}
        </dd>
      </div>

      <div className="sm:col-span-3">
        <dt className="text-sm font-medium text-gray-500">Details</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.issueDetails ?? "N/A"}
        </dd>
      </div>

      <div className="sm:col-span-3">
        <dt className="text-sm font-medium text-gray-500">Expectation</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.expectation ?? "N/A"}
        </dd>
      </div>

      <div className="sm:col-span-3">
        <dt className="text-sm font-medium text-gray-500">Attachments</dt>
        <dd className="mt-1 text-sm text-gray-900">
          {issue?.attachments?.length ? (
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
              {issue?.attachments?.map((documentUrl, idx) => {
                const documentName = lodash
                  .chain(documentUrl)
                  .split("/")
                  .last()
                  .value();
                return (
                  <li
                    key={idx}
                    className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                  >
                    <div className="flex w-0 flex-1 items-center">
                      <PaperClipIcon
                        aria-hidden="true"
                        className="h-5 w-5 flex-shrink-0 text-gray-400"
                      />
                      <span className="ml-2 w-0 flex-1 truncate">
                        {documentName}
                      </span>
                    </div>
                    <div className="ml-4 flex-shrink-0 space-x-3">
                      <FilePreview url={documentUrl} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="relative block w-full rounded-lg border-2 border-dashed border-gray-400 p-6 text-center">
              <DocumentTextIcon
                aria-hidden="true"
                strokeWidth={1}
                className="mx-auto text-sm h-8 w-8 text-gray-500"
              />
              <span className="mt-3 block text-sm text-gray-500">
                No Files Were Attached
              </span>
            </div>
          )}
        </dd>
      </div>
    </dl>
  );
};

export default IssueDetailsView;
